import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FeatureHintAnsicht } from "./FeatureHintAnsicht";
import { alsDeutschesDatum } from "./datum";
import { FeatureHintGroup, FeatureHintListItem } from "./useFeatureHints";
import { useFeatureHintsContext } from "./FeatureHintsContext";


/**
 * Wie eine Einordnung beim Kunden heisst. Exportiert, weil die Schreibmaske dieselben Woerter
 * zeigen muss: Die ganze Idee dieses Bildschirms ist, dass der Super-Admin sieht, was der
 * Kunde sieht. Zwei Listen wuerden die Vorschau beim ersten Aendern still unwahr machen.
 */
export const GROUP_LABELS: Record<FeatureHintGroup, string> = {
  neu: "Neue Funktion",
  verbessert: "Verbessert",
  behoben: "Behoben",
  erinnerung: "Erinnerung",
  wissenswert: "Wissenswert",
};

interface NeuigkeitenBereichProps {
  open: boolean;
  hints: FeatureHintListItem[];
  /** Waehrend einer Super-Admin-Impersonierung: ansehen ja, markieren nein (A7). */
  readOnly: boolean;
  onClose: () => void;
  onMarkRead: (key: string) => void;
  onMarkAllRead: () => void;
}

/**
 * Der dauerhafte Neuigkeiten-Bereich (KAN-251). Rechts angedockt, kein Modal und kein
 * Abdunkeln (A2) - ein einfach positioniertes Feld statt eines Dialogs, die Seite daneben
 * bleibt bedienbar.
 *
 * Gruppe und Datum jedes Eintrags kommen aus dem Datensatz (A9); dieses Bauteil sortiert
 * nur noch ein und blendet leere Gruppen aus (A3).
 *
 * Der Bereich haengt bewusst am `document.body` statt dort, wo er im Baum steht: Sonst ist
 * er ein Nachfahre der Seitenleiste, und CSS-`:hover` gilt fuer jeden Vorfahren. Die
 * Seitenleiste klappte dann auf, sobald der Mauszeiger ueber dem Bereich stand - obwohl der
 * ganz woanders liegt.
 */
export const NeuigkeitenBereich = ({ open, hints, readOnly, onClose, onMarkRead, onMarkAllRead }: NeuigkeitenBereichProps) => {
  const feld = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [offenerEintrag, setOffenerEintrag] = useState<string | null>(null);
  const { melden } = useFeatureHintsContext();

  // Aufgeklappt heisst gesehen - fuer alles, was dann in der Liste steht (KAN-296). Ein
  // Durchlauf je Oeffnen, nicht je Zeile: Der Server haelt ohnehin nur den ersten fest.
  useEffect(() => {
    if (!open || hints.length === 0) return;
    melden(hints.map(hint => ({ key: hint.key, art: "gesehen" as const })));
    // Absichtlich nur am Oeffnen haengend: Waechst die Liste waehrend des Lesens, ist das
    // kein zweites Sehen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Zugeklappt heisst wieder bei der Liste anfangen (KAN-255). Der Bereich wird beim
  // Schliessen nicht abgebaut, er gibt nur nichts mehr aus - ohne das hier ueberlebt der
  // geoeffnete Eintrag und steht beim naechsten Oeffnen erneut da, statt der Liste.
  useEffect(() => {
    if (!open) setOffenerEintrag(null);
  }, [open]);

  // Ein Klick daneben schliesst, Esc ebenso - wie man es von einem aufgeschlagenen Feld
  // erwartet. Der Klick auf den Schalter in der Seitenleiste zaehlt nicht als "daneben":
  // Sonst schloesse der Bereich hier zu und der Schalter oeffnete ihn im selben Zug wieder.
  useEffect(() => {
    if (!open) return undefined;

    const beiKlick = (event: MouseEvent) => {
      const ziel = event.target as HTMLElement | null;
      if (!ziel) return;
      if (feld.current?.contains(ziel)) return;
      if (ziel.closest('[data-schalter="neuigkeiten"]')) return;
      onClose();
    };

    const beiTaste = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", beiKlick);
    document.addEventListener("keydown", beiTaste);
    return () => {
      document.removeEventListener("mousedown", beiKlick);
      document.removeEventListener("keydown", beiTaste);
    };
  }, [open, onClose]);

  if (!open) return null;

  const unreadCount = hints.filter(hint => !hint.seen).length;
  const geoeffneter = hints.find(hint => hint.key === offenerEintrag);

  return createPortal(
    <div
      ref={feld}
      // z-[110] ist kein frei gewaehlter Wert: react-draft-wysiwyg gibt seiner Listen- und
      // Ausrichtungs-Auswahl z-index 90 und den aufgeklappten Listen 100. Darunter zeichnen
      // genau diese Knoepfe durch den Bereich hindurch, mitten in den Text.
      className="fixed top-0 right-0 bottom-0 w-[410px] max-w-[calc(100vw-82px)] bg-white border-l border-gray-200 shadow-[-4px_0_16px_rgba(0,0,0,0.08)] z-[110] flex flex-col"
      aria-label="Neuigkeiten-Bereich"
    >
      {/* Vollansicht statt Fenster darueber (A7-A10): Derselbe Bereich wechselt, die Liste
          ist einen Klick entfernt. Ein Dialog wuerde abdunkeln, was der Kunde gerade selbst
          geoeffnet hat, und zwei Mal Schliessen verlangen. */}
      {geoeffneter ? (
        <>
          <div className="p-3 border-b border-gray-100">
            <button
              type="button"
              onClick={() => setOffenerEintrag(null)}
              className="inline-flex items-center gap-2 min-h-9 px-2 rounded-lg text-sm font-semibold text-gray-600"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 5l-7 7 7 7" />
              </svg>
              Alle Neuigkeiten
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            <FeatureHintAnsicht
              inhalt={geoeffneter}
              ueberschrift={alsDeutschesDatum(geoeffneter.date)}
              onGriff={(art, stelle) => melden([{ key: geoeffneter.key, art, stelle }])}
              onInternal={href => {
                onClose();
                navigate(href);
              }}
            />
          </div>
        </>
      ) : (
        <>
      <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Neuigkeiten</h2>
          <p className="text-sm text-gray-500 mt-1">{unreadCount > 0 ? `${unreadCount} noch ungelesen` : "Alles gelesen"}</p>
        </div>
        {/* Nur solange es etwas zu markieren gibt: Ein Knopf, der nichts mehr tut, sieht
            aus wie einer, der nicht funktioniert. */}
        {!readOnly && unreadCount > 0 && (
          <button type="button" onClick={onMarkAllRead} className="whitespace-nowrap text-sm font-medium text-neuigkeit">
            Alle gelesen
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {hints.length === 0 && (
          <p className="px-5 py-6 text-sm text-gray-500">
            Zurzeit nichts Neues. Hier stehen künftig alle Änderungen an kandidat.io.
          </p>
        )}
        {/* Eine durchgehende Liste, neueste zuerst. Die Einordnung gruppiert hier nicht mehr:
            Bei wenigen Eintraegen stand ueber jedem eine eigene Ueberschrift mit einem
            Eintrag darunter, und das sortierte nichts (KAN-294). Sie fuehrt weiter die
            Fragen in der Schreibmaske. */}
        {hints.map(hint => (
                <button
                  key={hint.key}
                  type="button"
                  onClick={() => {
                    // Oeffnen geht immer, auch beim Blick in eine Kundenorga: Lesen ist kein
                    // Markieren. Gesperrt ist nur das Markieren - darum kuemmert sich
                    // `onMarkRead`, das im Nur-Ansehen-Fall nichts tut (A11).
                    setOffenerEintrag(hint.key);
                    melden([{ key: hint.key, art: "geoeffnet" }]);
                    if (!hint.seen) onMarkRead(hint.key);
                  }}
                  className={`relative w-full text-left pl-7 pr-5 py-3 ${!hint.seen ? "bg-blue-50" : ""}`}
                >
                  {/* Der Punkt sitzt in der Polsterung, nicht in einer eigenen Spalte: Sonst
                      ruecken die Titel ein und stehen nicht mehr buendig mit dem Datum am
                      rechten Rand. Die Polsterung links ist dafuer breiter als rechts. */}
                  {!hint.seen && (
                    <span
                      className="absolute left-3 top-5 block h-2 w-2 rounded-full bg-neuigkeit"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-medium text-gray-900">{hint.title}</span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{alsDeutschesDatum(hint.date)}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{hint.text}</p>
                  </div>
                </button>
        ))}
      </div>
        </>
      )}

    </div>,
    document.body
  );
};

export default NeuigkeitenBereich;
